<template>
  <!--
    Formulario de contacto "Personalizado" (Diseño 3 del formulario, panel → Inicio / Contacto): las preguntas, su tipo,
    su orden y cuáles son obligatorias las define la notaría en el panel (control.Campos). Mismo estilo que el guiado.
    Ref: https://trello.com/c/3wG1fWK4
  -->
  <div class="cg home-section" :class="{ 'form-en-plantilla': enPlantilla }" id="contact" :style="{ '--cg-acento': acento, '--cg-acento-texto': acentoTexto, '--cg-enlace': enlace, '--cg-texto': colorTexto }"
    :dir="IDIOMAS_RTL.includes(idiomaSolicitud) ? 'rtl' : 'ltr'">
    <v-container>
      <v-row class="cg__fila">
        <v-col :lg="conImagen ? 7 : 12" cols="12" :order="control.ImagenPosicion === 1 ? 2 : 1">
          <div class="cg__tarjeta">
            <div class="cg__cabecera" :style="{ textAlign: control.Titulo?.css?.Align }">
              <h2 class="cg__titulo" :style="tituloStyle" v-html="control.Titulo?.Texto"></h2>
              <p v-if="control.Descripcion?.Texto" class="cg__descripcion" :style="descripcionStyle" v-html="control.Descripcion.Texto"></p>
            </div>
            <ClientOnly>
              <div v-if="!textosListos" class="cg__cargando" />
              <div v-else-if="enviado" class="cg__ok" role="status">
                <v-icon size="44" color="success">mdi-check-circle-outline</v-icon>
                <h3>{{ t('Mensaje enviado') }}</h3>
                <p>{{ t('Le responderemos lo antes posible.') }}</p>
                <p><button type="button" class="cg__enlace" @click="reiniciar">{{ t('Enviar otro mensaje') }}</button></p>
              </div>
              <form v-else novalidate @submit.prevent="enviar">
                <template v-for="c in campos" :key="c.Id">
                  <!-- Bloque: título y texto que separan secciones -->
                  <div v-if="c.Tipo === 'BLOQUE'" class="cp__bloque">
                    <p v-if="c.Etiqueta" class="cg__pregunta">{{ c.Etiqueta }}</p>
                    <p v-if="c.Ayuda" class="cg__nota">{{ c.Ayuda }}</p>
                  </div>

                  <!-- Opción única -->
                  <fieldset v-else-if="c.Tipo === 'OPCION'" class="cg__campo cp__grupo" :aria-describedby="errores[c.Id] ? 'cp-err-' + c.Id : undefined">
                    <legend class="cg__etiqueta">{{ c.Etiqueta }}<span v-if="c.Obligatorio"> *</span></legend>
                    <p v-if="c.Ayuda" class="cg__ayuda">{{ c.Ayuda }}</p>
                    <label v-for="(o, i) in c.Opciones" :key="i" class="cp__opcion">
                      <input v-model="valores[c.Id]" type="radio" :name="'cp-' + c.Id" :value="o" /> <span>{{ o }}</span>
                    </label>
                    <p v-if="errores[c.Id]" :id="'cp-err-' + c.Id" class="cg__error">{{ errores[c.Id] }}</p>
                  </fieldset>

                  <!-- Casillas (varias) -->
                  <fieldset v-else-if="c.Tipo === 'CASILLAS'" class="cg__campo cp__grupo">
                    <legend class="cg__etiqueta">{{ c.Etiqueta }}<span v-if="c.Obligatorio"> *</span></legend>
                    <p v-if="c.Ayuda" class="cg__ayuda">{{ c.Ayuda }}</p>
                    <label v-for="(o, i) in c.Opciones" :key="i" class="cp__opcion">
                      <input v-model="marcadas[c.Id]" type="checkbox" :value="o" /> <span>{{ o }}</span>
                    </label>
                    <p v-if="errores[c.Id]" class="cg__error">{{ errores[c.Id] }}</p>
                  </fieldset>

                  <!-- Resto: una etiqueta y un control -->
                  <div v-else class="cg__campo">
                    <label class="cg__etiqueta" :for="'cp-' + c.Id">{{ c.Etiqueta }}<span v-if="c.Obligatorio"> *</span></label>
                    <p v-if="c.Ayuda" class="cg__ayuda">{{ c.Ayuda }}</p>
                    <textarea v-if="c.Tipo === 'TEXTO_LARGO'" :id="'cp-' + c.Id" v-model="valores[c.Id]" class="cg__input" rows="4" maxlength="3000"></textarea>
                    <select v-else-if="c.Tipo === 'DESPLEGABLE'" :id="'cp-' + c.Id" v-model="valores[c.Id]" class="cg__input">
                      <option value="">{{ t('Elija una opción') }}</option>
                      <option v-for="(o, i) in c.Opciones" :key="i" :value="o">{{ o }}</option>
                    </select>
                    <input v-else :id="'cp-' + c.Id" v-model="valores[c.Id]" class="cg__input" :type="tipoInput(c.Tipo)"
                      :autocomplete="autocompletar(c.Tipo)" :maxlength="c.Tipo === 'FECHA' ? undefined : (c.Tipo === 'EMAIL' ? 200 : 500)" />
                    <p v-if="errores[c.Id]" class="cg__error">{{ errores[c.Id] }}</p>
                  </div>
                </template>

                <!-- Honeypot -->
                <div class="cg__hp" aria-hidden="true"><label>Web <input v-model="honeypot" type="text" name="website" tabindex="-1" autocomplete="off"></label></div>

                <label class="cg__consentimiento">
                  <input v-model="consentimiento" type="checkbox" />
                  <span>{{ t('He leído y acepto la') }} <NuxtLink to="/protecciondatos" target="_blank">{{ t('política de privacidad') }}</NuxtLink></span>
                </label>
                <p v-if="errores.consentimiento" class="cg__error">{{ errores.consentimiento }}</p>

                <div v-if="errorEnvio" class="cg__aviso" role="alert">
                  <v-icon size="20">mdi-alert-circle-outline</v-icon>
                  <span>{{ errorEnvio }}
                    <a v-if="emailNotaria && !errorEnvio.endsWith('.')" :href="'mailto:' + emailNotaria">{{ emailNotaria }}</a>
                  </span>
                </div>

                <button type="submit" class="cg__boton" :style="botonStyle" :disabled="enviando">
                  <v-progress-circular v-if="enviando" indeterminate size="18" width="2" class="mr-2" />
                  {{ control.Button?.Texto || t('Enviar') }}
                </button>
              </form>
              <template #fallback><div class="cg__cargando" /></template>
            </ClientOnly>
          </div>
        </v-col>

        <v-col v-if="conImagen" lg="5" cols="12" class="d-none d-lg-block" :order="control.ImagenPosicion === 1 ? 1 : 2">
          <div class="cg__imagen"><img :src="imagenIDtoSRC(control.Imagen)" alt="" /></div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3'
import { useStore } from '../../store/index'
import { useUtils } from '#imports'
import { t, textosListos, cargarTextosSolicitud, idiomaSolicitud, IDIOMAS_RTL } from '../../composables/solicitudTextos'
import { apiSolicitudes } from '../../composables/useSolicitudWeb'
import { acentoLegible, textoLegibleSobre } from '../../composables/solicitudColor'
import { useEstiloFormulario } from '../../composables/estiloFormulario'

/** Campo tal como lo guarda el panel (Theme.*.Form.Campos). */
interface CampoFormulario { Id: string; Tipo: string; Etiqueta?: string; Ayuda?: string; Opciones?: string[]; Obligatorio?: boolean }

const props = defineProps<{ contactForm: any }>()
const control = computed(() => props.contactForm || {})
const campos = computed<CampoFormulario[]>(() => (control.value.Campos || []).filter((c: CampoFormulario) => c && c.Id && c.Tipo))

const store = useStore()
const route = useRoute()
const { imagenIDtoSRC } = useUtils()

if (import.meta.client) cargarTextosSolicitud()

// --- Estado ---------------------------------------------------------------------------------
const valores = reactive<Record<string, string>>({})
const marcadas = reactive<Record<string, string[]>>({})
function iniciarValores() {
  for (const c of campos.value) {
    if (c.Tipo === 'CASILLAS') marcadas[c.Id] = []
    else valores[c.Id] = ''
  }
}
iniciarValores()
const consentimiento = ref(false)
const honeypot = ref('')
const errores = reactive<Record<string, string>>({})
const enviando = ref(false)
const enviado = ref(false)
const errorEnvio = ref('')
const emailNotaria = computed(() => (store.NOTARIA as any)?.Contacto?.Email || '')
const origen = computed(() => route.path === '/' || /^\/[a-z]{2}\/?$/.test(route.path) ? 'FORM_INICIO' : 'FORM_CONTACTO')

const tipoInput = (tipo: string) => ({ EMAIL: 'email', TELEFONO: 'tel', FECHA: 'date' } as Record<string, string>)[tipo] || 'text'
const autocompletar = (tipo: string) => ({ NOMBRE: 'name', EMAIL: 'email', TELEFONO: 'tel' } as Record<string, string>)[tipo] || 'off'

// --- Estilo (el del panel o el de la plantilla, como los otros diseños) -----------------------
const { enPlantilla, estilo, acentoPlantilla } = useEstiloFormulario(() => control.value)
const acento = computed(() => enPlantilla.value ? acentoPlantilla.value : (control.value.Button?.css?.BackgroundColor || '#1f3a5f'))
const acentoTexto = computed(() => textoLegibleSobre(acento.value, enPlantilla.value ? undefined : control.value.Button?.css?.Color))
const enlace = computed(() => acentoLegible(acento.value))
const colorTexto = computed(() => enPlantilla.value ? 'var(--n2-texto)' : (control.value.Titulo?.css?.Color || '#1d2329'))
const conImagen = computed(() => !!control.value.Imagen)
const tituloStyle = computed(() => ({
  color: estilo.value.titulo.color, fontFamily: estilo.value.titulo.fuente,
  fontSize: estilo.value.titulo.tamano, fontWeight: estilo.value.titulo.peso
}))
const descripcionStyle = computed(() => ({
  color: estilo.value.descripcion.color, fontFamily: estilo.value.descripcion.fuente,
  fontSize: estilo.value.descripcion.tamano
}))
const botonStyle = computed(() => ({ backgroundColor: acento.value, color: acentoTexto.value, fontFamily: estilo.value.boton.fuente }))

// --- Acciones -----------------------------------------------------------------------------------
function limpiarErrores() { Object.keys(errores).forEach((k) => delete errores[k]) }

function reiniciar() {
  enviado.value = false
  consentimiento.value = false
  iniciarValores()
}

function validar() {
  limpiarErrores()
  for (const c of campos.value) {
    if (c.Tipo === 'BLOQUE') continue
    const vacio = c.Tipo === 'CASILLAS' ? !(marcadas[c.Id] || []).length : !(valores[c.Id] || '').trim()
    if (vacio) { if (c.Obligatorio) errores[c.Id] = t('Este dato es obligatorio'); continue }
    if (c.Tipo === 'EMAIL' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valores[c.Id].trim())) errores[c.Id] = t('El email no es válido')
    if (c.Tipo === 'TELEFONO' && valores[c.Id].replace(/\D/g, '').length < 8) errores[c.Id] = t('El teléfono no es válido')
  }
  if (!consentimiento.value) errores.consentimiento = t('Debe aceptar la política de privacidad para enviar')
  return Object.keys(errores).length === 0
}

// reCAPTCHA v3 (mismo grupo de claves que el resto de formularios)
let obtenerToken: (() => Promise<string>) | null = null
if (import.meta.client && store.recaptchaSiteKey) {
  const { vueApp } = useNuxtApp()
  if (!(vueApp.config.globalProperties as any).$recaptcha)
    vueApp.use(VueReCaptcha, { siteKey: store.recaptchaSiteKey, loaderOptions: { autoHideBadge: false } })
  const rc = useReCaptcha()
  if (rc) obtenerToken = async () => { await rc.recaptchaLoaded(); return rc.executeRecaptcha('email') }
}

const escHtml = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const sinHtml = (v: any) => String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

/** Pregunta y respuesta legibles (para el envío de respaldo por mailing). */
function respuestasLegibles(): { pregunta: string; respuesta: string; tipo: string }[] {
  return campos.value.filter((c) => c.Tipo !== 'BLOQUE').map((c) => {
    let r = c.Tipo === 'CASILLAS' ? (marcadas[c.Id] || []).join(', ') : (valores[c.Id] || '').trim()
    const m = c.Tipo === 'FECHA' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(r) : null
    if (m) r = `${m[3]}/${m[2]}/${m[1]}`
    return { pregunta: c.Etiqueta || '', respuesta: r, tipo: c.Tipo }
  }).filter((x) => x.respuesta)
}

/** Alternativa de envío si el servicio de contacto no responde: mailing + copia en el panel. Devuelve true si el correo salió. */
async function enviarPorMailing(token: string): Promise<boolean> {
  const n: any = store.NOTARIA
  const destino = n?.Contacto?.Email
  if (!destino) return false
  const resp = respuestasLegibles()
  const nombre = resp.find((r) => r.tipo === 'NOMBRE')?.respuesta || ''
  const email = resp.find((r) => r.tipo === 'EMAIL')?.respuesta || ''
  const telefono = resp.find((r) => r.tipo === 'TELEFONO')?.respuesta || ''
  const titulo = sinHtml(control.value.Titulo?.Texto) || 'Formulario'
  const cuerpo = `
    Mensaje enviado desde la web: ${escHtml(location.origin)}<br><br>
    <b>${escHtml(titulo)}</b><br><br>
    ${resp.map((r) => `${escHtml(r.pregunta)}: ${escHtml(r.respuesta).replace(/\n/g, '<br>')}`).join('<br><br>')}
    <br><br><b>Por favor, no responda a este correo. Este mensaje ha sido generado automáticamente.</b>
  `
  const textoPlano = resp.map((r) => `${r.pregunta}: ${r.respuesta}`).join('\n')
  const asunto = `Contacto WEB - ${n?.Nombre || ''} - [${titulo}]${nombre ? ' - ' + nombre : ''} - ${origen.value}`
  try {
    const modelo = { Subject: asunto, Name: nombre, Email: email, Phone: telefono, Message: textoPlano }
    const apiBase = (store.backend_api_url || (process.env.NODE_ENV === 'development' ? store.api_url : 'https://controlpanel.notin.es/')).replace(/\/+$/, '')
    const bodystr = btoa(unescape(encodeURIComponent(JSON.stringify(modelo))))
    $fetch(`${apiBase}/api/Emails/Public/${n?.id || n?.IdValue}`, { method: 'POST', body: { bodystr } }).catch((e) => console.warn('No se pudo registrar copia del formulario en el panel:', e))
  } catch { /* la copia en el panel es opcional */ }
  try {
    await $fetch('https://mailing.notin.es/api/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: {
        perfil: 'info_recaptcha_web_notaria',
        cuerpo,
        is_cuerpo_html: true,
        asunto,
        destinatarios: [{ nombre: n?.Nombre, direccion: destino }],
        destinatarios_cco: [
          { nombre: 'webNotin', direccion: 'web@notin.net' },
          { nombre: 'avisosUnidata', direccion: 'pwavisos@unidata.es' }
        ],
        recaptcha: { id_credential: n?.Personalizar?.RecaptchaGroup ?? null, token, action: 'email' },
        data: { id_notaria: n?.id, Fecha: new Date().toISOString(), Name: nombre, Email: email, Phone: telefono, Subject: asunto, Message: textoPlano }
      }
    })
    return true
  } catch (e) {
    console.error('Error de envío por mailing del formulario personalizado', e)
    return false
  }
}

async function enviar() {
  errorEnvio.value = ''
  if (!validar()) {
    errorEnvio.value = t('Revise los datos marcados.')
    return
  }
  enviando.value = true
  let token = ''
  try {
    try { token = obtenerToken ? await obtenerToken() : '' } catch { token = '' }
    const n: any = store.NOTARIA
    await $fetch(`${apiSolicitudes(store)}api/ContactoWeb/Public/${n?.id || n?.IdValue}/personalizado`, {
      method: 'POST',
      body: {
        origen: origen.value,
        valores: campos.value.filter((c) => c.Tipo !== 'BLOQUE').map((c) => c.Tipo === 'CASILLAS'
          ? { id: c.Id, valores: marcadas[c.Id] || [] }
          : { id: c.Id, valor: (valores[c.Id] || '').trim() }),
        idioma: idiomaSolicitud.value,
        consentimiento: consentimiento.value,
        website: honeypot.value,
        recaptcha: { id_credential: n?.Personalizar?.RecaptchaGroup ?? null, token, action: 'email' }
      }
    })
    enviado.value = true
  } catch (e: any) {
    const estado = e?.response?.status
    // 400: el servidor ha rechazado algún campo; se marcan los que diga
    const rechazados: string[] = e?.response?._data?.campos || []
    rechazados.forEach((id) => { errores[id] = t('Revise este dato') })
    if (estado !== 429 && estado !== 400 && !honeypot.value && await enviarPorMailing(token)) {
      enviado.value = true
      enviando.value = false
      return
    }
    errorEnvio.value = estado === 400 && rechazados.length ? t('Revise los datos marcados.')
      : estado === 429 ? t('Ha hecho demasiados envíos seguidos. Espere unos minutos y vuelva a intentarlo.')
      : emailNotaria.value ? t('No hemos podido enviar el mensaje. Inténtelo de nuevo o escríbanos a')
        : t('No hemos podido enviar el mensaje. Inténtelo de nuevo en unos minutos.')
  } finally {
    enviando.value = false
  }
}
</script>

<style lang="scss" scoped>
.cg {
  padding: 64px 0;
  color: var(--cg-texto);
  &__fila { align-items: stretch; }
  &__tarjeta {
    background: #fff; border: 1px solid #e3e7eb; border-radius: 20px; padding: 40px;
    box-shadow: 0 16px 40px rgba(20, 30, 40, .06); height: 100%;
    @media (max-width: 600px) { padding: 22px 16px; border-radius: 14px; }
  }
  &__cabecera { margin-bottom: 24px; }
  &__titulo { margin: 0 0 8px; line-height: 1.2; }
  &__descripcion { margin: 0; opacity: .85; }
  &__cargando { height: 260px; border-radius: 12px; background: linear-gradient(90deg, #f1f3f5 25%, #f7f8f9 50%, #f1f3f5 75%); background-size: 200% 100%; animation: cg-brillo 1.4s infinite linear; }
  &__pregunta { font-weight: 700; font-size: 17.5px; margin: 0 0 12px; color: #1d2329; }
  &__opcional { font-weight: 400; font-size: 13.5px; color: #6b7680; }
  &__motivos { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
    @media (max-width: 960px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 520px) { grid-template-columns: 1fr; } }
  &__motivo {
    display: flex; flex-direction: column; align-items: flex-start; gap: 4px; text-align: start;
    padding: 16px; border: 2px solid #e3e7eb; border-radius: 14px; background: #fff; cursor: pointer;
    transition: border-color .15s, box-shadow .15s, transform .15s; min-height: 110px;
    &:hover, &:focus-visible { border-color: var(--cg-acento); box-shadow: 0 6px 18px rgba(20, 30, 40, .08); transform: translateY(-1px); outline: none; }
  }
  &__motivo-icono { color: var(--cg-acento) !important; margin-bottom: 6px; }
  &__motivo-titulo { font-weight: 700; color: #1d2329; line-height: 1.25; }
  &__motivo-desc { font-size: 14px; color: #5f6b76; line-height: 1.35; }
  &__volver { display: inline-flex; align-items: center; gap: 6px; background: none; border: 0; padding: 0; margin-bottom: 18px; color: #5f6b76; cursor: pointer; font-size: 15px; }
  &__chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  &__chip {
    display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; border-radius: 999px;
    border: 1.5px solid #cfd6dc; background: #fff; color: #1d2329; cursor: pointer; font-size: 15px; min-height: 40px;
    &.activo { border-color: var(--cg-acento); background: var(--cg-acento); color: #fff; }
    &:focus-visible { outline: 3px solid color-mix(in srgb, var(--cg-acento) 40%, transparent); outline-offset: 2px; }
  }
  &__separador { width: 1px; background: #e3e7eb; margin: 0 4px; }
  &__campo { margin-bottom: 16px; }
  &__etiqueta { display: block; font-weight: 600; margin-bottom: 6px; color: #1d2329; }
  &__ayuda { font-size: 14px; color: #5f6b76; margin: -2px 0 8px; }
  &__ayuda--aviso { margin: 4px 0 0; padding: 8px 12px; border-radius: 10px; background: #fff8e1; color: #5d4500; }
  &__input {
    width: 100%; padding: 12px 14px; border: 1.5px solid #cfd6dc; border-radius: 10px; font-size: 16px; background: #fff; color: #1d2329;
    &:focus { border-color: var(--cg-acento); outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--cg-acento) 18%, transparent); }
  }
  &__campo-fecha {
    position: relative;
    display: flex;
    align-items: center;
  }
  &__input--fecha {
    padding-right: 42px;
    cursor: pointer;
    font-family: inherit;
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: auto;
      color: transparent;
      background: transparent;
      cursor: pointer;
    }
  }
  &__calendario { background: #fff; color: #1d2329; border: 1px solid #e3e7eb; border-radius: 14px; box-shadow: 0 16px 40px rgba(20, 30, 40, .16); max-width: calc(100vw - 32px); overflow: hidden;
    .v-date-picker { background: transparent; } }
  &__horas { padding: 0 16px 16px; max-width: 328px; }
  &__horas-titulo { margin: 0 0 8px; font-weight: 700; font-size: 15px; }
  &__horas-lista { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; max-height: 164px; overflow-y: auto;
    .cg__chip { justify-content: center; padding: 6px 4px; min-height: 34px; font-size: 14px; } }
  &__btn-calendario {
    position: absolute;
    right: 12px;
    background: none;
    border: 0;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--cg-acento);
    cursor: pointer;
    pointer-events: none;
    .v-icon { color: inherit !important; }
  }
  textarea.cg__input { resize: vertical; }
  &__rejilla { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 12px; @media (max-width: 760px) { grid-template-columns: 1fr; } }
  &__error { color: #b3261e; font-size: 14px; margin: 6px 0 0; }
  &__nota { color: #5f6b76; margin: 10px 0 14px; }
  &__online, &__nv {
    display: flex; gap: 14px; align-items: flex-start; border-radius: 14px; padding: 16px 18px; margin: 14px 0 6px;
    p { margin: 4px 0 12px; color: #3d4852; }
  }
  &__online { background: #eef6ff; border: 1px solid #cfe3fb; .v-icon { color: #1565c0 !important; } }
  &__nv { background: #f1f8f2; border: 1px solid #cfe6d3; margin-bottom: 18px; .v-icon { color: #2e7d32 !important; } }
  &__info { display: flex; flex-wrap: wrap; gap: 8px 22px; background: #f4f6f8; border-radius: 12px; padding: 12px 16px; margin-bottom: 18px; color: #1d2329; a { color: inherit !important; } }
  &__boton {
    display: inline-flex; align-items: center; justify-content: center; padding: 13px 28px; border: 0; border-radius: 999px;
    background: var(--cg-acento); color: #fff; font-weight: 700; font-size: 16px; cursor: pointer; text-decoration: none; min-height: 48px;
    &:disabled { opacity: .7; cursor: default; }
    &--secundario { padding: 10px 20px; min-height: 42px; font-size: 15px; }
    // Los botones-enlace (encargo online, Notaría Virtual) usan el texto legible del botón del panel. El CSS de cada notaría
    // pinta los <a> con el color del tema, que puede coincidir con el fondo y dejar el botón "sin texto"
    &--secundario, &--secundario:link, &--secundario:visited, &--secundario:hover, &--secundario:focus { color: var(--cg-acento-texto) !important; }
  }
  &__enlace { background: none; border: 0; color: var(--cg-acento); text-decoration: underline; cursor: pointer; }
  // El CSS de cada notaría puede pintar los <a> (y el texto) en blanco para fondos oscuros; la tarjeta siempre es blanca
  &__consentimiento { display: flex; gap: 10px; align-items: flex-start; margin: 18px 0 6px; cursor: pointer; color: #1d2329;
    a, a:visited, a:hover { color: var(--cg-enlace) !important; text-decoration: underline; }
    input { width: 20px; height: 20px; margin-top: 2px; accent-color: var(--cg-acento); } }
  &__aviso { display: flex; gap: 8px; align-items: flex-start; background: #fdecea; color: #7a1c14; border-radius: 10px; padding: 12px 14px; margin: 14px 0; a { color: inherit !important; } }
  &__ok { text-align: center; padding: 30px 10px; h3 { margin: 10px 0 6px; } p { color: #5f6b76; } }
  &__referencia { strong { color: #1d2329; letter-spacing: .02em; } }
  &__docs { background: #f7f8fa; border: 1px solid #e3e7eb; border-radius: 12px; padding: 12px 16px; margin: 6px 0 18px; color: #1d2329;
    ul { margin: 6px 0 0; padding-inline-start: 22px; } li { margin: 2px 0; }
    .v-icon { color: var(--cg-acento) !important; } }
  &__docs-titulo { display: flex; align-items: center; gap: 8px; font-weight: 700; margin: 0; }
  &__hp { position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden; }
  // La ilustración no se estira con el formulario: altura fija, arriba, como en el diseño Moderno
  &__imagen { height: 520px; max-height: 70vh; border-radius: 20px; overflow: hidden; img { width: 100%; height: 100%; object-fit: cover; } }
  // El tema de las webs fuerza un tamaño de letra en todos los <p> con más especificidad que una sola clase
  & &__ayuda, & &__error { font-size: 14px; }
  & &__pregunta { font-size: 17px; }
}
.cp__bloque { margin: 22px 0 6px; &:first-child { margin-top: 0; } }
.cp__grupo { border: 0; padding: 0; min-width: 0; }
.cp__opcion { display: flex; gap: 10px; align-items: center; padding: 6px 0; cursor: pointer; color: #1d2329;
  input { width: 18px; height: 18px; margin: 0; accent-color: var(--cg-acento); } }
// El tema de las webs pone a los <select> y <p> una altura y un color propios: aquí se fijan
.cg select.cg__input { appearance: auto; height: auto; min-height: 48px; line-height: 1.4; padding-top: 10px; padding-bottom: 10px; }
.cg p.cg__error { color: #b3261e !important; }
@keyframes cg-brillo { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
</style>
